// src/pages/offlineSales/components/CustomerStatementPrintModal.jsx
// Cloned from OrderReceiptPrintModal.jsx's pattern — same shared PrintPreviewModal with a
// receipt/A4 toggle and a PDF download button, reusing its CSS module (class names are
// receipt-generic, not order-specific).
import React from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { exportCustomerStatementPDF } from '../../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../../utils/themeColors';
import PrintPreviewModal from '../../../components/common/PrintPreviewModal/PrintPreviewModal';
import CustomerStatementA4 from './CustomerStatementA4';
import styles from './OrderReceiptPrintModal.module.css';

const TYPE_LABELS = {
  invoice: 'Invoice',
  payment: 'Payment Received',
  credit_note: 'Credit Note',
  return: 'Refund (Cash)',
};

const CustomerStatementPrintModal = ({ statement, onClose }) => {
  const { t, formatCurrency } = useLocalization();

  if (!statement) return null;

  const renderReceipt = () => (
    <div className={styles.receiptArea}>
      <h2 className={styles.receiptTitle}>{t('offlineSales.customerStatement', 'Customer Statement')}</h2>
      <p className={styles.receiptShop}>{statement.customer?.name}</p>

      <div className={styles.receiptMeta}>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.selectDateRange', 'Period')}</span>
          <span>{statement.dateFrom || '—'} — {statement.dateTo || '—'}</span>
        </div>
        <div className={styles.receiptMetaRow}>
          <span>{t('offlineSales.openingBalance', 'Opening Balance')}</span>
          <span>{formatCurrency(statement.openingBalance)}</span>
        </div>
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptItems}>
        {(statement.transactions || []).map((tx, index) => (
          <div key={index} className={styles.receiptItem}>
            <span className={styles.receiptItemName}>
              {new Date(tx.date).toLocaleDateString()} — {TYPE_LABELS[tx.type] || tx.type} ({tx.reference || '—'})
            </span>
            <span className={styles.receiptItemTotal}>
              {tx.debit > 0 ? formatCurrency(tx.debit) : `-${formatCurrency(tx.credit)}`}
            </span>
          </div>
        ))}
      </div>

      <hr className={styles.receiptDivider} />

      <div className={styles.receiptTotals}>
        <div className={`${styles.receiptTotalRow} ${styles.receiptGrandTotal}`}>
          <strong>{t('offlineSales.closingBalance', 'Closing Balance')}</strong>
          <strong>{formatCurrency(statement.closingBalance)}</strong>
        </div>
      </div>
    </div>
  );

  return (
    <PrintPreviewModal
      title={`${t('offlineSales.customerStatement', 'Customer Statement')} — ${statement.customer?.name || ''}`}
      onClose={onClose}
      pdfFileName={`Customer_Statement_${statement.customer?.name || "Customer"}.pdf`}
      onDownloadPdf={() => exportCustomerStatementPDF(statement, {
        primary: getCssVar('--color-primary', '#4f46e5'),
        primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
      })}
      renderReceipt={renderReceipt}
      renderA4={() => <CustomerStatementA4 statement={statement} />}
    />
  );
};

export default CustomerStatementPrintModal;
